/**
 * Tenant scope of report widgets.
 *
 * A report never reads "every tenant": each widget owns the tenants in its data
 * binding, and without any it cannot load. The editor therefore fills that scope
 * when a widget is added, from the dashboard's own tenant setting, so an author
 * never meets a widget that fails only because a technical field was left empty.
 */
import type { JsonValue } from "@nexus/widget-sdk";

import type { Tenant } from "@/types";
import { requireTenantIds } from "@/lib/live/data-sources/reports";
import { DATA_REGISTRY } from "./DataRegistry";
import type { WidgetPurpose } from "./catalogPresentation";
import type { DashboardContext } from "./dashboardContext";
import { normalizeDataBinding, type WidgetDataBinding, type WidgetInstance } from "./types";
import { getWidget } from "./WidgetRegistry";

type Bindings = NonNullable<WidgetInstance["dataBindings"]>;

function hasTenantScope(params: Record<string, JsonValue> | undefined): boolean {
  try {
    requireTenantIds(params?.tenant_ids);
    return true;
  } catch {
    return false;
  }
}

/**
 * The widget's configurable data requests that read a tenant-scoped report, with what they
 * currently resolve to. Report-edition snapshots are left out: the report page supplies their scope.
 */
function reportRequests(widgetId: string, dataBindings: WidgetInstance["dataBindings"]) {
  return Object.entries(getWidget(widgetId)?.manifest?.dataRequests ?? {}).flatMap(
    ([key, request]) => {
      if (!request.configurable) return [];
      const binding = normalizeDataBinding(
        dataBindings?.[key] ?? { source: request.source, params: request.params },
      );
      const source = binding.source;
      if (!DATA_REGISTRY[source]?.report?.paramKeys.includes("tenant_ids")) return [];
      return [{ ...binding, key, params: binding.params ?? request.params }];
    },
  );
}

/** Whether the widget reads a report and has no tenant to read it for yet. */
export function needsTenantScope(
  widgetId: string,
  dataBindings: WidgetInstance["dataBindings"],
): boolean {
  return reportRequests(widgetId, dataBindings).some((request) => !hasTenantScope(request.params));
}

/** Whether the widget reads at least one tenant-scoped report. */
export function readsTenantReport(
  widget: Pick<WidgetInstance, "widgetId" | "dataBindings">,
): boolean {
  return reportRequests(widget.widgetId, widget.dataBindings).length > 0;
}

function tenantIdsOf(params: Record<string, JsonValue> | undefined): number[] {
  const value = params?.tenant_ids;
  return Array.isArray(value)
    ? value.filter((entry): entry is number => typeof entry === "number").sort((a, b) => a - b)
    : [];
}

export function usesDashboardTenants(
  binding: WidgetDataBinding | undefined,
  dashboardTenantIds: readonly number[],
): boolean {
  const normalized = binding ? normalizeDataBinding(binding) : undefined;
  if (normalized?.tenantScope === "custom") return false;
  if (normalized?.tenantScope === "dashboard") return true;
  return (
    tenantIdsOf(normalized?.params).join() ===
    [...dashboardTenantIds].sort((left, right) => left - right).join()
  );
}

/** The tenants one widget's reports read, `[]` when none or when its reports disagree. */
export function widgetReportTenantIds(
  widget: Pick<WidgetInstance, "widgetId" | "dataBindings">,
): number[] {
  const sets = reportRequests(widget.widgetId, widget.dataBindings).map((request) =>
    tenantIdsOf(request.params),
  );
  return sets.length > 0 && sets.every((ids) => ids.join() === sets[0].join()) ? sets[0] : [];
}

export interface ReportTenantState {
  /** Widgets reading a tenant-scoped report. */
  widgets: number;
  /** The tenants they share; with `mixed`, the most common set. */
  ids: number[];
  /** Report widgets read different tenants. */
  mixed: boolean;
}

/**
 * Summarize report tenant coverage; exclude explicit custom overrides when inferring
 * a legacy dashboard's inherited tenants, but include them when detecting mixed scope.
 */
export function reportTenantState(
  widgets: readonly Pick<WidgetInstance, "widgetId" | "dataBindings">[],
  includeCustom = true,
): ReportTenantState {
  const requests = (widget: Pick<WidgetInstance, "widgetId" | "dataBindings">) =>
    reportRequests(widget.widgetId, widget.dataBindings).filter(
      (request) => includeCustom || request.tenantScope !== "custom",
    );
  const sets = widgets.flatMap((widget) =>
    requests(widget).map((request) => tenantIdsOf(request.params).join(",")),
  );
  const count = widgets.filter((widget) => requests(widget).length > 0).length;
  if (sets.length === 0) return { widgets: 0, ids: [], mixed: false };
  const tally = new Map<string, number>();
  for (const key of sets) tally.set(key, (tally.get(key) ?? 0) + 1);
  const [common] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0];
  return {
    widgets: count,
    ids: common ? common.split(",").map(Number) : [],
    mixed: tally.size > 1,
  };
}

/** Apply tenant coverage, optionally preserving custom bindings during dashboard edits. */
export function withReportTenants<T extends Pick<WidgetInstance, "widgetId" | "dataBindings">>(
  widget: T,
  tenantIds: readonly number[],
  preserveCustom = false,
): T {
  const requests = reportRequests(widget.widgetId, widget.dataBindings);
  if (requests.length === 0) return widget;
  const dataBindings = { ...widget.dataBindings };
  for (const request of requests) {
    if (preserveCustom && request.tenantScope === "custom") {
      dataBindings[request.key] = {
        source: request.source,
        tenantScope: request.tenantScope,
        params: request.params,
      };
      continue;
    }
    dataBindings[request.key] = {
      source: request.source,
      ...(request.tenantScope ? { tenantScope: "dashboard" as const } : {}),
      params: {
        ...request.params,
        tenant_ids: [...tenantIds].sort((a, b) => a - b),
      },
    };
  }
  return { ...widget, dataBindings };
}

/**
 * The data bindings a widget starts with when added to a dashboard.
 *
 * In a report dashboard a widget that can read either a live or a report source starts on the
 * first report source, so it never opens on a kind of data the dashboard is not about. A report
 * source is given the tenants offered. `undefined` when the widget's own defaults are right.
 */
export function initialDataBindings(
  widgetId: string,
  { purpose, tenantIds }: { purpose: WidgetPurpose; tenantIds: readonly number[] },
): Bindings | undefined {
  const bindings: Bindings = {};
  for (const [key, request] of Object.entries(getWidget(widgetId)?.manifest?.dataRequests ?? {})) {
    if (!request.configurable) continue;
    let source = request.source;
    if (purpose === "reporting" && !DATA_REGISTRY[source]?.report) {
      const report = Object.entries(DATA_REGISTRY).find(
        ([name, candidate]) =>
          // A widget listing its sources only ever starts on one of them.
          (!request.sources || request.sources.includes(name)) &&
          // Never a report-edition snapshot: it only has data on a report page.
          candidate.report?.paramKeys.includes("tenant_ids") &&
          candidate.contract.name === request.contract.name &&
          candidate.contract.version === request.contract.version,
      );
      if (report) source = report[0];
    }
    if (!DATA_REGISTRY[source]?.report) {
      if (source !== request.source) bindings[key] = { source };
      continue;
    }
    const seeded = tenantIds.length > 0;
    if (source === request.source && !seeded) continue;
    bindings[key] = {
      source,
      ...(source !== request.source || seeded
        ? {
            params: {
              ...(source === request.source ? request.params : undefined),
              ...(seeded ? { tenant_ids: [...tenantIds] } : {}),
            },
          }
        : {}),
    };
  }
  return Object.keys(bindings).length > 0 ? bindings : undefined;
}

/**
 * Up to this many readable tenants, a report widget starts on all of them: nothing to decide, and
 * a query that stays small. Beyond it the author has to say which tenants, so a large account is
 * never queried across everything by default. Same rule for every profile.
 */
export const REPORT_DEFAULT_TENANT_LIMIT = 5;

/**
 * The tenants a widget added to this dashboard starts with: those the dashboard is set to cover
 * ("All my tenants" means every readable one), otherwise the tenant selection, otherwise every
 * readable tenant when there are few enough (see {@link REPORT_DEFAULT_TENANT_LIMIT}).
 */
export function defaultReportTenantIds({
  context,
  tenants,
  selectedTenantIds,
}: {
  context: Pick<DashboardContext, "tenants">;
  tenants: readonly Tenant[];
  selectedTenantIds: readonly string[];
}): number[] {
  const readable = tenants
    .map((tenant) => Number(tenant.tenant_id))
    .filter((id) => Number.isInteger(id) && id > 0);
  const chosen = selectedTenantIds.map(Number).filter((id) => readable.includes(id));
  const ids =
    context.tenants === "all"
      ? readable
      : chosen.length > 0
        ? chosen
        : readable.length <= REPORT_DEFAULT_TENANT_LIMIT
          ? readable
          : [];
  return [...ids].sort((a, b) => a - b);
}
